import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Moment from "moment";
import Icon from "../Icon/Icon";
import InputDatePicker from "../Inputs/InputDatePicker";
import "./ManagementList.scss";

const PAGE_SIZE = 10;

interface Props {
  arrData: any[];
  typeComponent: string;
  iconName: "calendar" | "euro";
  variant?: "blue" | "amber";
}

const ManagementList: React.FC<Props> = ({ arrData, typeComponent, iconName, variant = "blue" }) => {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const q = search.trim().toLowerCase();

  const matchesDate = (item: any): boolean => {
    if (!dateFilter) return true;
    if (!item.fecha) return false;
    const d = new Date(item.fecha);
    const [dd, mm, yyyy] = dateFilter.split("/");
    return (
      d.getFullYear() === parseInt(yyyy) &&
      d.getMonth() === parseInt(mm) - 1 &&
      d.getDate() === parseInt(dd)
    );
  };

  const filtered = (arrData || []).filter((item: any) => {
    if (!matchesDate(item)) return false;
    if (!q) return true;
    return (
      item.nombre?.toLowerCase().includes(q) ||
      item.empresa?.toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const handleClick = (item: any) => {
    navigate(`/historial/${params.id}/${typeComponent}/${item.tipo_componente}__${item.id}`);
  };

  const isAmber = variant === "amber";

  return (
    <div className="mgmt-list">
      <div className="mgmt-list__filters">
        <div className="mgmt-list__search">
          <Icon name="search" size={16} />
          <input
            className="mgmt-list__search-input"
            type="text"
            placeholder="Buscar por nombre o empresa…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            autoComplete="off"
            spellCheck={false}
          />
          {search && (
            <button
              className="mgmt-list__search-clear"
              onClick={() => { setSearch(""); setPage(1); }}
              aria-label="Limpiar búsqueda"
            >
              <Icon name="x" size={14} />
            </button>
          )}
        </div>
        <div className="mgmt-list__date-wrap">
          <InputDatePicker
            id="mgmt-date"
            name="mgmt-date"
            placeholder="Filtrar por fecha"
            value={dateFilter}
            onChange={(val) => { setDateFilter(val); setPage(1); }}
          />
          {dateFilter && (
            <button
              className="mgmt-list__search-clear mgmt-list__date-clear"
              onClick={() => { setDateFilter(null); setPage(1); }}
              aria-label="Limpiar fecha"
            >
              <Icon name="x" size={14} />
            </button>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="mgmt-list__empty">
          {q || dateFilter ? "Sin resultados para la búsqueda actual." : "No hay datos para mostrar."}
        </p>
      ) : (
        <>
          <div className="mgmt-list__cards">
            {paged.map((item: any) => (
              <article
                key={item.id}
                className={`mgmt-list-card${isAmber ? " mgmt-list-card--amber" : ""}`}
                role="button"
                tabIndex={0}
                onClick={() => handleClick(item)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleClick(item);
                  }
                }}
              >
                <div className={`mgmt-list-card__icon${isAmber ? " mgmt-list-card__icon--amber" : ""}`}>
                  <Icon name={iconName} size={22} />
                </div>
                <div className="mgmt-list-card__content">
                  <div className="mgmt-list-card__name">{item.nombre}</div>
                  {item.empresa && (
                    <div className="mgmt-list-card__empresa">{item.empresa}</div>
                  )}
                </div>
                {item.fecha && (
                  <div className="mgmt-list-card__date">
                    <span className={`mgmt-date-chip${isAmber ? " mgmt-date-chip--amber" : ""}`}>
                      <Icon name="clock" size={11} />
                      <span>{Moment(item.fecha).format("DD MMM YYYY")}</span>
                    </span>
                  </div>
                )}
              </article>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="mgmt-list__pager">
              <span className="mgmt-list__pager-info">
                {(safePage - 1) * PAGE_SIZE + 1}–
                {Math.min(safePage * PAGE_SIZE, filtered.length)} de {filtered.length}
              </span>
              <div className="mgmt-list__pager-btns">
                <button
                  className="btn-box btn-outline-accent"
                  disabled={safePage <= 1}
                  onClick={() => setPage(safePage - 1)}
                >
                  <Icon name="arrow-left" size={16} /> Anterior
                </button>
                <button
                  className="btn-box btn-outline-accent"
                  disabled={safePage >= totalPages}
                  onClick={() => setPage(safePage + 1)}
                >
                  Siguiente <Icon name="arrow-right" size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ManagementList;
